import { useParams } from 'react-router'

import Button from '@/elements/Button'
import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import FormSearchableSelect from '@/elements/FormSearchableSelect'
import Loading from '@/elements/Loading'
import Modal from '@/components/Modal'
import Table from '@/components/Table'
import Pagination from '@/components/Pagination'
import useBreakpoints from '@/hooks/useBreakpoints'
import { useAssignments } from '@/hooks/useAssignments'
import { useMemberIndividualsTable } from '@/hooks/tables/memberIndividualsTable'
import type { AssignedSubjectResponse, SubjectResponse } from '@/api/types/Subject'

function MemberAssignments() {
  const { isTablet, isMobile } = useBreakpoints()
  const { id: orgMemberIdParam } = useParams<{ id: string }>()
  const orgMemberId = orgMemberIdParam || ''

  const {
    assignments,
    availableAssignments,
    updateAvailableAssignmentsSearch,
    pagination,
    isLoadingAssignments,
    isLoadingAvailableAssignments,
    setPage,
    handleCreate,
    openDeleteModal,
    closeDeleteModal,
    showDeleteModal,
    handleDelete,
  } = useAssignments({
    userId: orgMemberId,
    type: 'INDIVIDUAL',
    paginationKey: 'memberIndividuals',
    successMessages: {
      create: 'Individual assigned successfully',
      delete: 'Individual(s) unassigned successfully'
    },
    errorMessages: {
      create: 'Failed to assign individual',
      delete: 'Failed to unassign individual(s)'
    }
  })

  const assignedIndividualsTableData = useMemberIndividualsTable(
    assignments as AssignedSubjectResponse[],
    openDeleteModal
  )

  const handleAssignIndividual = async (individual: SubjectResponse | null) => {
    if (!individual?.id) return
    await handleCreate({
      individualId: individual.id
    })
  }

  return (
    <Flex
      wfull
      className='card'
      align='flex-end'
      gap={8}
      p={isMobile ? 3 : isTablet ? 5 : 7}
    >
      <Flex wfull inline={!isTablet} gap={4} wrap>
        <Flex fraction={4} gap={2} wfull>
          <Text variant='h3'>Individuals</Text>
          <Text color='black-light-40'>
            Update members's assigned individuals.
          </Text>
        </Flex>
        <Flex fraction={6} wfull hfull gap={5}>
          <FormSearchableSelect<SubjectResponse>
            name='individualAssignment'
            isUserSearch
            autoSubmit={false}
            options={availableAssignments as SubjectResponse[]}
            submitLabel='Assign'
            placeholder='Search individuals...'
            optionLabel={item => item.user.details.fullName}
            optionValue={item => item.id}
            optionImage={item => item.user.assets?.PROFILE_PICTURE || ''}
            updateSearch={q => updateAvailableAssignmentsSearch('name', q)}
            loadingOptions={isLoadingAvailableAssignments}
            onSubmit={handleAssignIndividual}
          />
          {isLoadingAssignments ? (
            <Flex align='center' justify='center' wfull py={8}>
              <Loading />
            </Flex>
          ) : (
            <Flex wfull gap={3}>
              {assignments && assignments.length > 0 && (
                <Flex inline wfull justify='end'>
                  <Button
                    variant='textOnlyDanger'
                    onClick={() => openDeleteModal()}
                  >
                    Unassign All Individuals
                  </Button>
                </Flex>
              )}
              <Flex wfull align='center' gap={3}>
                <Table {...assignedIndividualsTableData} />
                <Modal
                  isOpen={showDeleteModal}
                  title='Unassign Individual(s)'
                  onSave={handleDelete}
                  onCancel={closeDeleteModal}
                  cancelLabel='No'
                  saveLabel='Yes'
                >
                  <Text color='black' style={{ hyphens: 'none' }}>
                    Are you sure you want to unassign individual(s)? This action cannot be undone.
                  </Text>
                </Modal>
                <Pagination
                  variant={isMobile ? 'compact' : 'default'}
                  onPageChange={setPage}
                  {...pagination}
                />
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default MemberAssignments
